import { useEffect, useState, Fragment } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Send, Trash2, Megaphone, X, Sparkles, History, KeyRound, ChevronDown, ChevronUp, Pencil, Plus, TrendingUp } from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastVersion, setBroadcastVersion] = useState(0);
  const [expandedNews, setExpandedNews] = useState(null); // news sub id
  const [editingNews, setEditingNews] = useState(null); // nsub object
  const [stocksRefreshing, setStocksRefreshing] = useState(false);
  const [stocksSnapshot, setStocksSnapshot] = useState(null);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // AuthCallback handles
    (async () => {
      try {
        // 401 is expected when not signed in — accept it silently to avoid console noise.
        const r = await axios.get(`${API}/admin-auth/me`, {
          withCredentials: true,
          validateStatus: (s) => s === 200 || s === 401,
        });
        setUser(r.status === 200 ? r.data : null);
      } catch { setUser(null); }
      finally { setChecking(false); }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/subscribers`, { withCredentials: true });
        setRows(r.data);
      } catch { toast.error("Failed to load subscribers"); }
    })();
  }, [user]);

  const login = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await axios.post(`${API}/admin-auth/logout`, {}, { withCredentials: true }); } catch (e) { console.warn("logout failed:", e); }
    setUser(null);
  };

  const inviteAddPasswords = async () => {
    // Two-step confirmation. First a dry-run to show recipient count.
    try {
      const dry = await axios.post(`${API}/admin/invite-set-password`, { dry_run: true }, { withCredentials: true });
      const n = dry.data?.would_send_to ?? 0;
      if (n === 0) {
        toast.success("Everyone is already on the new auth — nothing to send.");
        return;
      }
      const ok = window.confirm(
        `Email ${n} subscriber${n === 1 ? "" : "s"} an "Add a password" link?\n\n` +
        "Each email contains a one-time, 7-day token that lets the recipient set a password and instantly access /app with their existing locations.\n\n" +
        "Subscribers who already have a password set are skipped automatically."
      );
      if (!ok) return;
      const r = await axios.post(`${API}/admin/invite-set-password`, {}, { withCredentials: true });
      toast.success(`Sent ${r.data.sent} of ${r.data.recipient_count}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite send failed.");
    }
  };

  const sendNow = async (subId) => {
    try {
      await axios.post(`${API}/admin/subscriptions/${subId}/send-now`, {}, { withCredentials: true });
      toast.success("Sent.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };
  const deleteSub = async (subId) => {
    if (!window.confirm("Delete this subscription permanently?")) return;
    try {
      await axios.delete(`${API}/admin/subscriptions/${subId}`, { withCredentials: true });
      toast.success("Deleted.");
      reload();
    } catch { toast.error("Delete failed"); }
  };
  const toggleActive = async (sub) => {
    try {
      await axios.put(`${API}/admin/subscriptions/${sub.id}`, { active: !sub.active }, { withCredentials: true });
      reload();
    } catch { toast.error("Update failed"); }
  };
  const toggleVerified = async (subr) => {
    try {
      await axios.put(`${API}/admin/subscribers/${subr.email}`, { verified: !subr.verified }, { withCredentials: true });
      reload();
    } catch { toast.error("Update failed"); }
  };
  const toggleNewsActive = async (nsub) => {
    try {
      await axios.put(`${API}/admin/news/${nsub.id}`, { active: !nsub.active }, { withCredentials: true });
      reload();
    } catch { toast.error("Update failed"); }
  };
  const deleteNewsSub = async (id) => {
    if (!window.confirm("Delete this news subscription permanently?")) return;
    try {
      await axios.delete(`${API}/admin/news/${id}`, { withCredentials: true });
      toast.success("Deleted.");
      reload();
    } catch { toast.error("Delete failed"); }
  };
  const sendNewsNow = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/news/${id}/send-now`, {}, { withCredentials: true });
      toast.success(`Sent ${r.data?.articles || 0} stories.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };
  const deleteSubscriber = async (email) => {
    if (!window.confirm(`Hard-delete ${email} and ALL their weather + news subscriptions? This cannot be undone.`)) return;
    try {
      const r = await axios.delete(`${API}/admin/subscribers/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success(`Deleted ${email} (${r.data?.deleted_weather || 0} weather, ${r.data?.deleted_news || 0} news).`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const reload = async () => {
    const r = await axios.get(`${API}/admin/subscribers`, { withCredentials: true });
    setRows(r.data);
  };

  const backfillLegacy = async () => {
    if (!window.confirm("Create subscriber records for any legacy subscriptions without them? (Safe to run — idempotent.)")) return;
    try {
      const r = await axios.post(`${API}/admin/backfill-legacy`, {}, { withCredentials: true });
      const n = r.data?.backfilled ?? 0;
      toast.success(
        n === 0
          ? "No legacy rows to back-fill — all subscribers already exist."
          : `Back-filled ${n} subscriber${n === 1 ? "" : "s"}. Total: ${r.data.total_subscribers}.`
      );
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Back-fill failed");
    }
  };

  // Top-10 Stocks manual refresh — runs the full Perplexity pipeline
  // synchronously (~30-90s depending on how many firms return data).
  // Overwrites today's snapshot in `stock_consensus_snapshots` and
  // returns the fresh payload so we can preview it inline without
  // needing to jump to /app.
  const refreshStocksNow = async () => {
    if (!window.confirm("Run the Top-10 Stocks consensus refresh now? This calls Perplexity ~13 times (~$0.13 cost) and overwrites today's snapshot. Takes 30-90 seconds.")) return;
    setStocksRefreshing(true);
    setStocksSnapshot(null);
    try {
      const r = await axios.post(`${API}/stocks/refresh-now`, {}, { withCredentials: true });
      setStocksSnapshot(r.data);
      const count = r.data?.top10?.length ?? 0;
      toast.success(`Fresh snapshot for ${r.data?.date}: ${count} tickers ranked.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stocks refresh failed");
    } finally {
      setStocksRefreshing(false);
    }
  };

  if (checking) return <Shell><Loader2 className="w-6 h-6 animate-spin" /></Shell>;

  if (!user) {
    return (
      <Shell>
        <div className="max-w-md">
          <h1 className="font-display text-4xl font-black tracking-tighter">Admin</h1>
          <p className="mt-4 text-neutral-700">Sign in with your Google account to manage Weathervane subscribers.</p>
          <button onClick={login} data-testid="admin-google-login" className="btn-brutal mt-8 px-6 py-3 text-sm tracking-[0.14em] uppercase font-semibold">Sign in with Google</button>
          <p className="mt-4 text-xs text-neutral-500">Only allowed Google accounts can access this page.</p>
        </div>
      </Shell>
    );
  }

  const filtered = !rows ? null : rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.email || "").toLowerCase().includes(s)
      || (r.first_name || "").toLowerCase().includes(s)
      || r.subscriptions?.some((x) => (x.location_label || "").toLowerCase().includes(s))
      || r.news_subscriptions?.some((n) => (n.refined_prompt || "").toLowerCase().includes(s));
  });

  return (
    <Shell>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="ticker-label">Admin</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Subscribers</h1>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-neutral-600">{user.email}</span>
          <button onClick={() => setShowBroadcast(true)} data-testid="admin-open-broadcast" title="Email subscribers about new features" className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFD600] border border-black hover:bg-black hover:text-[#FFD600] text-xs uppercase tracking-wider font-bold"><Megaphone className="w-3 h-3" />Broadcast</button>
          <button onClick={refreshStocksNow} data-testid="admin-refresh-stocks" disabled={stocksRefreshing} title="Run the Top-10 Stocks Perplexity refresh now (overwrites today's snapshot)" className="inline-flex items-center gap-2 px-4 py-2 border border-black hover:bg-[#FFD600] text-xs uppercase tracking-wider font-bold disabled:opacity-60 disabled:cursor-not-allowed">
            {stocksRefreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
            {stocksRefreshing ? "Refreshing…" : "Refresh Stocks"}
          </button>
          <button onClick={inviteAddPasswords} data-testid="admin-invite-passwords" title="Email all legacy email-only subscribers a one-time link to set a password and migrate to JWT auth" className="inline-flex items-center gap-2 px-4 py-2 border border-black hover:bg-[#FFD600] text-xs uppercase tracking-wider font-bold"><KeyRound className="w-3 h-3" />Invite to add password</button>
          <button onClick={backfillLegacy} data-testid="admin-backfill-legacy" title="Import legacy subscription records as subscribers" className="inline-flex items-center gap-2 px-4 py-2 border border-black hover:bg-[#FFD600] text-xs uppercase tracking-wider">Back-fill legacy</button>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2 border border-black hover:bg-[#FFD600] text-xs uppercase tracking-wider"><LogOut className="w-3 h-3" />Logout</button>
        </div>
      </div>
      {showBroadcast && <BroadcastModal onClose={(sent) => { setShowBroadcast(false); if (sent) setBroadcastVersion((v) => v + 1); }} /> }
      {editingNews && <NewsEditModal nsub={editingNews} onClose={(saved) => { setEditingNews(null); if (saved) reload(); }} />}
      {stocksSnapshot && (
        <div className="mt-6 border-2 border-black bg-white" data-testid="admin-stocks-snapshot">
          <div className="flex items-center justify-between bg-[#0A0A0A] text-white px-4 py-2">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold">
              Top-10 Stocks &middot; {stocksSnapshot.date} &middot; {stocksSnapshot.top10?.length ?? 0} ranked
            </div>
            <button onClick={() => setStocksSnapshot(null)} className="text-white/70 hover:text-white text-xs">Close</button>
          </div>
          {(stocksSnapshot.adds?.length || stocksSnapshot.removals?.length) ? (
            <div className="px-4 py-2 text-xs border-b border-black/10">
              {stocksSnapshot.adds?.length ? <span className="text-green-800 font-semibold mr-4">↑ New: {stocksSnapshot.adds.join(", ")}</span> : null}
              {stocksSnapshot.removals?.length ? <span className="text-red-800 font-semibold">↓ Off: {stocksSnapshot.removals.join(", ")}</span> : null}
            </div>
          ) : null}
          <table className="w-full text-left text-sm">
            <thead className="bg-neutral-100 text-[10px] uppercase tracking-[0.18em]">
              <tr><th className="p-2 w-10">#</th><th className="p-2">Ticker</th><th className="p-2">Company</th><th className="p-2 w-16 text-center">Score</th><th className="p-2">Firms</th></tr>
            </thead>
            <tbody>
              {(stocksSnapshot.top10 || []).map((t, i) => (
                <tr key={t.ticker} className={i < 3 ? "bg-[#FFFBE6]" : ""}>
                  <td className="p-2 font-mono text-neutral-500">{String(i + 1).padStart(2, "0")}</td>
                  <td className="p-2 font-mono font-bold">{t.ticker}</td>
                  <td className="p-2 text-neutral-700">{t.name || "—"}</td>
                  <td className="p-2 text-center"><span className="inline-block px-2 py-0.5 bg-black text-[#FFD600] font-mono text-xs">{t.score}</span></td>
                  <td className="p-2 text-[11px] text-neutral-600">{Array.from(new Set((t.firms || []).map((f) => f.firm))).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <BroadcastHistory refreshKey={broadcastVersion} />
      <input
        data-testid="admin-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by email, name, or location…"
        className="w-full px-4 py-3 border border-black outline-none text-[15px] bg-white focus:bg-[#FFFBE6] mb-6"
      />
      {!filtered ? <Loader2 className="w-6 h-6 animate-spin" /> : (
        <div className="space-y-4" data-testid="admin-subscribers-list">
          {filtered.length === 0 && <div className="text-neutral-500">No subscribers.</div>}
          {filtered.map((s) => (
            <div key={s.email} data-testid={`subscriber-${s.email}`} className="border border-black bg-white">
              <div className="flex items-center justify-between p-4 border-b border-black/10">
                <div>
                  <div className="font-semibold">{s.first_name} <span className="font-normal text-neutral-500">·</span> {s.email}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {s.verified ? "✓ Verified" : "⚠ Unverified"} · {(s.subscriptions || []).length} weather · {(s.news_subscriptions || []).length} news
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleVerified(s)} className="text-xs uppercase tracking-wider px-3 py-1 border border-black hover:bg-[#FFD600]">{s.verified ? "Unverify" : "Verify"}</button>
                  <button onClick={() => deleteSubscriber(s.email)} data-testid={`admin-delete-subscriber-${s.email}`} title="Hard-delete this user + all their subscriptions" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider px-3 py-1 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"><Trash2 className="w-3 h-3" /> Delete user</button>
                </div>
              </div>
              {(s.subscriptions || []).length > 0 && (
                <table className="w-full text-sm">
                  <thead><tr className="text-[10px] uppercase tracking-wider text-neutral-500">
                    <th className="text-left p-3">Weather Location</th><th className="text-left p-3">Time</th><th className="text-left p-3">Timezone</th><th className="text-left p-3">Active</th><th className="text-right p-3">Actions</th>
                  </tr></thead>
                  <tbody>
                    {s.subscriptions.map((sub) => (
                      <tr key={sub.id} className="border-t border-black/5">
                        <td className="p-3">{sub.location_label}</td>
                        <td className="p-3 font-mono">{sub.delivery_time}</td>
                        <td className="p-3 text-neutral-500 text-xs">{sub.timezone}</td>
                        <td className="p-3"><button onClick={() => toggleActive(sub)} className="text-xs uppercase tracking-wider px-2 py-1 border border-black hover:bg-[#FFD600]">{sub.active ? "Active" : "Paused"}</button></td>
                        <td className="p-3 text-right">
                          <button onClick={() => sendNow(sub.id)} title="Send now" className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-black hover:text-white"><Send className="w-3 h-3" /></button>
                          <button onClick={() => deleteSub(sub.id)} title="Delete" className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-red-600 hover:text-white"><Trash2 className="w-3 h-3" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {(s.news_subscriptions || []).length > 0 && (
                <table className="w-full text-sm border-t-2 border-black/10">
                  <thead><tr className="text-[10px] uppercase tracking-wider text-neutral-500 bg-[#FFFBE6]">
                    <th className="text-left p-3">News Interest</th><th className="text-left p-3">Time</th><th className="text-left p-3">Timezone</th><th className="text-left p-3">Active</th><th className="text-right p-3">Actions</th>
                  </tr></thead>
                  <tbody>
                    {s.news_subscriptions.map((nsub) => (
                      <Fragment key={nsub.id}>
                        <tr className="border-t border-black/5" data-testid={`admin-news-row-${nsub.id}`}>
                          <td className="p-3 max-w-md">
                            <button
                              onClick={() => setExpandedNews(expandedNews === nsub.id ? null : nsub.id)}
                              data-testid={`admin-news-expand-${nsub.id}`}
                              className="flex items-start gap-2 text-left w-full group"
                              title={expandedNews === nsub.id ? "Collapse" : "Show full prompt"}
                            >
                              {expandedNews === nsub.id
                                ? <ChevronUp className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                : <ChevronDown className="w-3.5 h-3.5 mt-0.5 shrink-0 text-neutral-400 group-hover:text-black" />}
                              <span className="line-clamp-2 text-[13px] text-neutral-700 italic group-hover:text-black">&ldquo;{nsub.refined_prompt}&rdquo;</span>
                            </button>
                          </td>
                          <td className="p-3 font-mono">{nsub.delivery_time}</td>
                          <td className="p-3 text-neutral-500 text-xs">{nsub.timezone}</td>
                          <td className="p-3"><button onClick={() => toggleNewsActive(nsub)} className="text-xs uppercase tracking-wider px-2 py-1 border border-black hover:bg-[#FFD600]">{nsub.active ? "Active" : "Paused"}</button></td>
                          <td className="p-3 text-right whitespace-nowrap">
                            <button onClick={() => setEditingNews(nsub)} data-testid={`admin-news-edit-${nsub.id}`} title="Edit prompt / topics" className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-[#FFD600]"><Pencil className="w-3 h-3" /></button>
                            <button onClick={() => sendNewsNow(nsub.id)} data-testid={`admin-news-send-now-${nsub.id}`} title="Send now" className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-black hover:text-white"><Send className="w-3 h-3" /></button>
                            <button onClick={() => deleteNewsSub(nsub.id)} title="Delete" className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-red-600 hover:text-white"><Trash2 className="w-3 h-3" /></button>
                          </td>
                        </tr>
                        {expandedNews === nsub.id && (
                          <tr className="border-t border-black/5 bg-[#FFFBE6]/60" data-testid={`admin-news-detail-${nsub.id}`}>
                            <td colSpan={5} className="p-4">
                              <div className="ticker-label !text-[10px] text-neutral-500 mb-1">Full prompt</div>
                              <div className="text-[13px] text-neutral-800 whitespace-pre-wrap mb-3">{nsub.refined_prompt || "—"}</div>
                              {(nsub.topics || []).length > 0 && (
                                <>
                                  <div className="ticker-label !text-[10px] text-neutral-500 mb-1">Topics ({nsub.topics.length})</div>
                                  <ul className="space-y-1">
                                    {nsub.topics.map((t, i) => (
                                      <li key={i} className="text-[13px] text-neutral-800">
                                        <span className="font-semibold">{(nsub.topic_labels || [])[i] || `Topic ${i + 1}`}:</span>{" "}
                                        <span className="text-neutral-600">{t}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                              <button onClick={() => setEditingNews(nsub)} className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 border border-black text-xs uppercase tracking-wider hover:bg-[#FFD600]"><Pencil className="w-3 h-3" /> Edit prompt</button>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <RoosterLogo size={36} />
            <div className="font-display text-2xl font-black tracking-tighter">Weathervane</div>
          </Link>
        </div>
      </header>
      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-12">{children}</section>
    </div>
  );
}

function NewsEditModal({ nsub, onClose }) {
  const [prompt, setPrompt] = useState(nsub.refined_prompt || "");
  const [topics, setTopics] = useState(() =>
    (nsub.topics || []).map((t, i) => ({ topic: t, label: (nsub.topic_labels || [])[i] || "" }))
  );
  const [saving, setSaving] = useState(false);
  const hasTopics = topics.length > 0;

  const setTopicField = (idx, field, value) =>
    setTopics((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  const removeTopic = (idx) => setTopics((prev) => prev.filter((_, i) => i !== idx));
  const addTopic = () => setTopics((prev) => [...prev, { topic: "", label: "" }]);

  const save = async () => {
    const body = { refined_prompt: prompt.trim() };
    if (hasTopics) {
      const cleaned = topics.filter((t) => t.topic.trim());
      if (!cleaned.length) { toast.error("At least one topic is required."); return; }
      body.topics = cleaned.map((t) => t.topic.trim());
      body.topic_labels = cleaned.map((t) => t.label.trim());
    }
    if (!body.refined_prompt && !hasTopics) { toast.error("Prompt can't be empty."); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/news/${nsub.id}`, body, { withCredentials: true });
      toast.success("Saved — caches cleared. Hit Send now to test the new prompt.");
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="news-edit-modal">
      <div className="bg-white border-2 border-black w-full max-w-3xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-black bg-[#FFD600]">
          <div className="flex items-center gap-2">
            <Pencil className="w-5 h-5" />
            <div className="font-display text-2xl font-black tracking-tighter">Edit news prompt</div>
          </div>
          <button onClick={() => onClose(false)} data-testid="news-edit-close" className="p-1 hover:bg-black hover:text-[#FFD600]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="text-xs text-neutral-500">{nsub.email}</div>
          <div>
            <label className="ticker-label !text-[10px] block mb-2">Refined prompt</label>
            <textarea
              data-testid="news-edit-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              maxLength={1000}
              className="w-full px-4 py-2.5 border border-black outline-none text-[14px] bg-white focus:bg-[#FFFBE6] resize-y"
            />
            <div className="text-[11px] text-neutral-400 mt-1">{prompt.length}/1000</div>
          </div>
          {hasTopics && (
            <div>
              <label className="ticker-label !text-[10px] block mb-2">Topics (each becomes its own section + search)</label>
              <div className="space-y-2">
                {topics.map((row, i) => (
                  <div key={i} className="flex items-start gap-2" data-testid={`news-edit-topic-${i}`}>
                    <input
                      value={row.label}
                      onChange={(e) => setTopicField(i, "label", e.target.value)}
                      placeholder="Label (shown in email)"
                      className="w-56 px-3 py-2 border border-black outline-none text-[13px] bg-white focus:bg-[#FFFBE6]"
                      data-testid={`news-edit-topic-label-${i}`}
                    />
                    <input
                      value={row.topic}
                      onChange={(e) => setTopicField(i, "topic", e.target.value)}
                      placeholder="Topic / search intent"
                      className="flex-1 px-3 py-2 border border-black outline-none text-[13px] bg-white focus:bg-[#FFFBE6]"
                      data-testid={`news-edit-topic-input-${i}`}
                    />
                    <button onClick={() => removeTopic(i)} title="Remove topic" className="p-2 hover:bg-red-600 hover:text-white border border-transparent"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              {topics.length < 10 && (
                <button onClick={addTopic} data-testid="news-edit-add-topic" className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 border border-black text-xs uppercase tracking-wider hover:bg-[#FFD600]"><Plus className="w-3 h-3" /> Add topic</button>
              )}
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-black/10">
            <div className="text-[11px] text-neutral-500 max-w-sm">Saving clears this feed&rsquo;s render cache &mdash; use &ldquo;Send now&rdquo; or the user&rsquo;s dashboard preview to test immediately.</div>
            <div className="flex items-center gap-2">
              <button onClick={() => onClose(false)} className="px-4 py-2 border border-black text-xs uppercase tracking-wider hover:bg-neutral-100">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="news-edit-save" className="btn-brutal px-5 py-2 text-xs uppercase tracking-[0.14em] inline-flex items-center gap-2">
                {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BroadcastModal({ onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("both");
  const [sinceDate, setSinceDate] = useState("");
  const [onlyVerified, setOnlyVerified] = useState(true);
  const [preview, setPreview] = useState(null); // { html, recipient_count, audience_label }
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const buildPayload = () => ({
    subject: subject.trim(),
    body_markdown: body,
    audience,
    since_date: sinceDate || null,
    only_verified: onlyVerified,
  });

  const handlePreview = async () => {
    if (!subject.trim() && !body.trim() && !sinceDate) {
      toast.error("Set a 'features added since' date to auto-draft, or fill subject + body.");
      return;
    }
    setLoading(true);
    setPreview(null);
    try {
      const r = await axios.post(`${API}/admin/broadcast/preview`, buildPayload(), { withCredentials: true });
      setPreview(r.data);
      // Backend may have auto-filled subject/body from the changelog —
      // reflect that back into the form so the admin can edit before send.
      if (r.data.auto_filled) {
        if (!subject.trim() && r.data.subject) setSubject(r.data.subject);
        if (!body.trim() && r.data.body_markdown) setBody(r.data.body_markdown);
        toast.success("Auto-drafted from changelog. Review and tweak before sending.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoFill = async () => {
    if (!sinceDate) {
      toast.error("Pick a 'features added since' date first.");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/broadcast/draft`, { since_date: sinceDate, audience }, { withCredentials: true });
      if (r.data?.subject) setSubject(r.data.subject);
      if (r.data?.body_markdown) setBody(r.data.body_markdown);
      toast.success(`Drafted from ${r.data.entries_count} changelog entr${r.data.entries_count === 1 ? "y" : "ies"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-draft failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/broadcast`, buildPayload(), { withCredentials: true });
      toast.success(`Broadcast sent to ${r.data.sent} of ${r.data.recipient_count}.`);
      onClose?.(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="broadcast-modal">
      <div className="bg-white border-2 border-black w-full max-w-5xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-black bg-[#FFD600]">
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5" />
            <div className="font-display text-2xl font-black tracking-tighter">Feature update broadcast</div>
          </div>
          <button onClick={onClose} data-testid="broadcast-close" className="p-1 hover:bg-black hover:text-[#FFD600]"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          <div className="p-6 border-r border-black/10 space-y-4">
            <div>
              <label className="ticker-label !text-[10px] block mb-2">Subject</label>
              <input
                data-testid="broadcast-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What's new in Weathervane"
                className="w-full px-4 py-2.5 border border-black outline-none text-[15px] bg-white focus:bg-[#FFFBE6]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="ticker-label !text-[10px] block mb-2">Audience</label>
                <select
                  data-testid="broadcast-audience"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  className="w-full px-3 py-2.5 border border-black outline-none text-[14px] bg-white focus:bg-[#FFFBE6]"
                >
                  <option value="both">All subscribers</option>
                  <option value="weather">Weather only</option>
                  <option value="news">News only</option>
                </select>
              </div>
              <div>
                <label className="ticker-label !text-[10px] block mb-2">Features added since</label>
                <input
                  type="date"
                  data-testid="broadcast-since-date"
                  value={sinceDate}
                  onChange={(e) => setSinceDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-black outline-none text-[14px] bg-white focus:bg-[#FFFBE6]"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="broadcast-only-verified"
                checked={onlyVerified}
                onChange={(e) => setOnlyVerified(e.target.checked)}
              />
              Only verified subscribers
            </label>

            <div>
              <label className="ticker-label !text-[10px] block mb-2">Body (Markdown)</label>
              <textarea
                data-testid="broadcast-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={"## Big stuff\n- New feature A\n- New feature B\n\nLearn more at [our blog](https://example.com)."}
                rows={14}
                className="w-full px-4 py-2.5 border border-black outline-none text-[14px] font-mono bg-white focus:bg-[#FFFBE6] resize-none"
              />
              <p className="text-xs text-neutral-500 mt-1">Supports **bold**, *italic*, `code`, [links](https://...), bullet lists with `-`, and ## / ### headings.</p>
            </div>

            <div className="flex items-center gap-3 pt-2 flex-wrap">
              <button
                onClick={handleAutoFill}
                disabled={loading || !sinceDate}
                data-testid="broadcast-autofill-btn"
                title={sinceDate ? "Auto-draft body from changelog entries since the chosen date" : "Pick a 'features added since' date first"}
                className="inline-flex items-center gap-2 px-4 py-3 text-xs tracking-[0.14em] uppercase font-semibold border border-black bg-white hover:bg-[#FFD600] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-3 h-3" />
                Auto-draft from changelog
              </button>
              <button
                onClick={handlePreview}
                disabled={loading}
                data-testid="broadcast-preview-btn"
                className="btn-brutal px-5 py-3 text-xs tracking-[0.14em] uppercase font-semibold inline-flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Preview &amp; recipient count
              </button>
            </div>
          </div>

          <div className="p-6 bg-[#F9F9F9]">
            {!preview && !loading && (
              <div className="text-center py-20 text-neutral-500 text-sm">
                Fill out the form and click <strong>Preview</strong> to see the rendered email and audience size.
              </div>
            )}
            {loading && (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin mb-3" />
                <div className="ticker-label">Rendering preview…</div>
              </div>
            )}
            {preview && (
              <div className="space-y-4" data-testid="broadcast-preview">
                <div className="border border-black bg-white p-4">
                  <div className="ticker-label !text-[10px] mb-2">Audience</div>
                  <div className="font-display text-2xl font-black tracking-tighter" data-testid="broadcast-recipient-count">
                    {preview.recipient_count} {preview.recipient_count === 1 ? "person" : "people"}
                  </div>
                  <div className="text-sm text-neutral-600 mt-1">
                    {preview.audience_label}{preview.since_human ? ` · since ${preview.since_human}` : ""}
                  </div>
                </div>

                <div className="border border-black bg-white">
                  <div className="px-4 py-2 border-b border-black bg-black text-[#FFD600] text-[10px] uppercase tracking-[0.18em]">
                    Email preview
                  </div>
                  <iframe
                    title="Broadcast email preview"
                    data-testid="broadcast-preview-iframe"
                    srcDoc={preview.html}
                    sandbox=""
                    className="w-full h-[480px] bg-white"
                  />
                </div>

                <button
                  onClick={() => setConfirming(true)}
                  disabled={preview.recipient_count === 0 || sending}
                  data-testid="broadcast-send-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 bg-black text-[#FFD600] hover:bg-[#FFD600] hover:text-black border-2 border-black text-sm tracking-[0.18em] uppercase font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  Send to {preview.recipient_count} {preview.recipient_count === 1 ? "person" : "people"}
                </button>
              </div>
            )}
          </div>
        </div>

        {confirming && (
          <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="broadcast-confirm">
            <div className="bg-white border-2 border-black p-6 max-w-md w-full">
              <div className="ticker-label mb-2">Confirm</div>
              <h3 className="font-display text-2xl font-black tracking-tighter mb-3">
                Send to {preview?.recipient_count} {preview?.recipient_count === 1 ? "person" : "people"}?
              </h3>
              <p className="text-sm text-neutral-700 mb-6">
                This will email <strong>{preview?.audience_label}</strong> immediately. There is no undo.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setConfirming(false)}
                  className="px-4 py-2.5 border border-black hover:bg-neutral-100 text-xs uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending}
                  data-testid="broadcast-confirm-send"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-[#FFD600] hover:bg-[#FFD600] hover:text-black border-2 border-black text-xs tracking-[0.18em] uppercase font-bold disabled:opacity-50"
                >
                  {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  {sending ? "Sending…" : "Yes, send now"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function BroadcastHistory({ refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/broadcasts`, { withCredentials: true });
        if (!cancelled) setItems(r.data?.broadcasts || []);
      } catch { if (!cancelled) setItems([]); }
    })();
    return () => { cancelled = true; };
  }, [open, refreshKey]);

  return (
    <div className="border border-black bg-white mb-6" data-testid="broadcast-history">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="broadcast-history-toggle"
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[#FFFBE6]"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4" />
          <div className="font-display text-lg font-black tracking-tighter">Broadcast history</div>
          {items && (
            <span className="text-xs text-neutral-500 ml-2">({items.length})</span>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">{open ? "hide" : "show"}</span>
      </button>
      {open && (
        <div className="border-t border-black/10">
          {items === null && (
            <div className="px-5 py-6 flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
          )}
          {items?.length === 0 && (
            <div className="px-5 py-6 text-sm text-neutral-500">No broadcasts sent yet.</div>
          )}
          {items?.length > 0 && (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-neutral-500 bg-[#FFFBE6]">
                <th className="text-left p-3">Subject</th>
                <th className="text-left p-3">Audience</th>
                <th className="text-left p-3">Since date</th>
                <th className="text-right p-3">Recipients</th>
                <th className="text-right p-3">Sent</th>
                <th className="text-right p-3">When</th>
              </tr></thead>
              <tbody>
                {items.map((b) => {
                  const when = b.sent_at ? new Date(b.sent_at) : null;
                  return (
                    <tr key={b.id} className="border-t border-black/5" data-testid={`broadcast-history-row-${b.id}`}>
                      <td className="p-3 max-w-md truncate" title={b.subject}>{b.subject}</td>
                      <td className="p-3 capitalize text-neutral-700">{b.audience}</td>
                      <td className="p-3 text-neutral-600 text-xs font-mono">{b.since_date || "—"}</td>
                      <td className="p-3 text-right tabular-nums">{b.recipient_count}</td>
                      <td className="p-3 text-right tabular-nums">
                        {b.sent}
                        {b.failed_count ? <span className="text-red-600 ml-1">(-{b.failed_count})</span> : null}
                      </td>
                      <td className="p-3 text-right text-neutral-600 text-xs whitespace-nowrap" title={b.sent_at}>
                        {when ? when.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
